/* eslint-disable max-len */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ServiceReportExtended } from '@sdir/httpclient/lib/clients/aps/seagoing';
import { ServiceReportQualificationResult } from '@sdir/httpclient/lib/clients/aps/application';
import { seagoingServicesApi, qualificationsApi } from '../../../httpclient';
import {
  AddedQualificationsContext,
  IAddedQualificationsContext,
} from '../../../services/contexts';
import { useCurrentApplication } from '../../../services/hooks';
import { Loadable } from '../../../types';
import { enabledNewSeagoing } from '../../../helpers/Qualifications';
import { DEFAULT_LANG } from '../../../helpers/languageHelpers';

export const AddedQualificationsProvider: React.FC<{ children: any }> = ({ children }) => {
  const intl = useIntl();
  const { application } = useCurrentApplication();
  const currentApplicationUid = useRef<string | null>();
  const [seagoingData, setSeagoingData] = useState<Loadable<ServiceReportExtended[]>>({});
  // eslint-disable-next-line prettier/prettier
  const [newSeagoingData, setNewSeagoingData] = useState<Loadable<ServiceReportQualificationResult>>({});

  const loadSeagoingDataForReference = useCallback((applicationReference: string) => {
    setSeagoingData({ loading: true });
    seagoingServicesApi
      .v1SeagoingservicesGetapplicationreferenceQueryPost({ applicationReference })
      .then((r) => setSeagoingData({ result: r.data }))
      .catch((e) => {
        setSeagoingData({ error: e });
      });
  }, []);

  const loadNewSeagoingData = useCallback((uid, lang) => {
    setNewSeagoingData({ loading: true });
    qualificationsApi
      .v1QualificationsGetservicereportqualificationsApplicationUidGet(uid)
      .then((r) => setNewSeagoingData({ result: r?.data }))
      .catch((e) => {
        setNewSeagoingData({ error: e });
      });
  }, []);

  const loadAddedSeagoing = useCallback(() => {
    if (!application.result?.uid) return;
    loadSeagoingDataForReference(application.result?.uid);
  }, [application.result?.uid, loadSeagoingDataForReference]);

  const loadNewAddedSeagoing = useCallback(() => {
    if (!application.result?.uid) return;
    const newSeagoing = enabledNewSeagoing();
    if (newSeagoing) {
      loadNewSeagoingData(application.result.uid, intl.locale || DEFAULT_LANG);
    }
  }, [application.result?.uid, intl, loadNewSeagoingData]);

  useEffect(() => {
    if (application.result && currentApplicationUid.current !== application.result?.uid) {
      currentApplicationUid.current = application.result.uid || '';
      setSeagoingData({});
    }
  }, [application.result]);

  const value = useMemo<IAddedQualificationsContext>(
    () => ({
      isReady: !!application.result?.uid,
      addedSeagoing: seagoingData,
      addedNewSeagoing: newSeagoingData,
      loadAddedSeagoing,
      loadNewAddedSeagoing,
    }),
    [
      application.result?.uid,
      seagoingData,
      newSeagoingData,
      loadAddedSeagoing,
      loadNewAddedSeagoing,
    ]
  );

  return (
    <AddedQualificationsContext.Provider value={value}>
      {children}
    </AddedQualificationsContext.Provider>
  );
};
